import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { InputGroup, Panel, Tab, Col, Row, Nav, NavItem } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseProductsTemplatesStore } from '../../store/providers/FranchiseProductsTemplates';
import { useFranchiseAccountingStore } from '../../store/providers/FranchiseAccounting';

import Permission from '../../components/Permission/Permission';
import { franchiseProductTemplateSelect } from '../../store/actions/FranchiseProductTemplate';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './FranchiseProductTemplateListSchema';
import { columns_feetype, sorting_feetype } from './FeeTypeListSchema';
import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import SideMenu from '../../components/SideMenu/SideMenu';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useLookupTypesStore } from '../../store/providers/LookupType';
import FormInput from '../../components/FormInput/FormInput';
import OptionsHelper from '../../lib/OptionsHelper';
import DocumentSetupList from '../../components/Lists/DocumentSetupList/DocumentSetupList';
import FeeTypeModal from './FeeTypeModal';

const FranchiseProductTemplateModal = React.lazy(() => import('../../components/Modals/FranchiseProductTemplateModal/FranchiseProductTemplateModal'));

function FranchiseProductsTemplates(props) {
    const dispatch = useDispatch();
    const { franchise } = useSelector(state => ({
        franchise: state.franchise.franchise
    }));

    // Store state
    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useFranchiseProductsTemplatesStore();
    const { listRecords: productTemplateTypeList, data: productTemplateTypeData } = useLookupTypesStore();
    const { listRecords: feeTypeList, data: feeTypeData } = useFranchiseAccountingStore();

    // Local state
    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isFeeTypeEditing, setIsFeeTypeEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isFeeTypeBusy, setIsFeeTypeBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [productTemplateTypeOptions, setProductTemplateTypeOptions] = useState([]);
    const [selectedType, setSelectedType] = useState('');
    const [selectedFeeType, setSelectedFeeType] = useState('');
    const [feeTypeListData, setFeeTypeListData] = useState([]);
    const [feeTypeListDataFiltered, setFeeTypeListDataFiltered] = useState([]);
    const [feeTypeItem, setFeeTypeItem] = useState('');
    const [selectedTab, setSelectedTab] = useState('subscriptions');
    const [feeTypeTypeOptions, setFeeTypeTypeOptions] = useState([]);

    
    const pagePermission = constants.PERMISSION_ACCOUNTING_FRANCHISEPRODUCTS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const tabSelectedHandler = (tabKey) => {
        setSelectedTab(tabKey);
        dispatch(breadcrumbRemoveRoute());
        updateBreadcrumbs(tabKey);
    };

    const updateBreadcrumbs = useCallback((tabKey) => {
        if (tabKey === 'subscriptions') {
            dispatch(breadcrumbAddRoute({ key: 'franchise-subscriptions', text: 'Subscriptions' }));
        } else {
            dispatch(breadcrumbAddRoute({ key: 'franchise-fees', text: 'Fee Mapping' }));
        }
    }, [dispatch]);

    const selectedHandler = (franchiseProduct) => {
        setIsEditing(true);
        dispatch(franchiseProductTemplateSelect({ franchiseProductId: franchiseProduct.id }));
    };

    const selectedFeeTypeHandler = (feeType) => {
        setIsFeeTypeEditing(true);
        setFeeTypeItem(feeType);
    };

    const addedHandler = () => {
        setIsEditing(true);
        dispatch(franchiseProductTemplateSelect());
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const refreshedFeeTypeHandler = () => {
        feeTypeList({franchiseId: 0});
    };

    const deletedHandler = (franchiseProduct) => {
        deleteRecord({ franchiseProductId: franchiseProduct.id });
    };

    const submittedHandler = () => {
        setIsEditing(false);
        setTimeout(() => {
            refreshedHandler();
        }, 1200);

    };
    const submittedFeeTypeHandler = () => {        
        setIsFeeTypeEditing(false);
        refreshedFeeTypeHandler();
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };
    const cancelledFeeTypeHandler = () => {
        setIsFeeTypeEditing(false);
    };

    const assignedHandler = () => {
        refreshedHandler();
    };
    const assignedFeeTypeHandler = () => {
        refreshedFeeTypeHandler();
    };

    const changedHandler = (id, value) => {
        if (id === 'typeId') {
            setSelectedType(parseInt(value));
            listRecords();
        }

        if (id === 'feeTypeId') {
            setSelectedFeeType(value);
        }
    }

    const moreFilters = (productTemplateTypes) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Subscription Type:</InputGroup.Addon>
                        <FormInput 
                            id="typeId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedType || ''}
                        >
                            {productTemplateTypes}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    const typeFilters = (feeTypes) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Fee Type:</InputGroup.Addon>
                        <FormInput 
                            id="feeTypeId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedFeeType || ''}
                        >
                            {feeTypes}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                if(selectedType && selectedType > 0){
                    var listInfo = data.body.filter((e) => e.productTypeId == selectedType);
                    setListData(listInfo);
                } else {
                    setListData(data.body);
                }
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list')));
    }, [data, addAlert, clearData, listRecords, franchise]);

    useEffect(() => {
        if (!_.isEmpty(productTemplateTypeData) && productTemplateTypeData.scope === 'list' && productTemplateTypeData.status === 'success') {
            setProductTemplateTypeOptions(OptionsHelper({ list: productTemplateTypeData.body, includeBlank: true, includeBlankName: 'All' }));
        }
    }, [productTemplateTypeData, props]);

    useEffect(() => {
        if (!_.isEmpty(feeTypeData) && feeTypeData.scope === 'list' && feeTypeData.status === 'success') {
            var listFeeTypeInfo = feeTypeData.body.netSuiteFeeItemMappings.filter((e) => e.fromTable != 'FranchiseProducts' && e.fromTable != 'AgentProducts');
            setFeeTypeListData(listFeeTypeInfo);

            let feeTypeIds = [];
            let listFeeTypeTypes = [];
            listFeeTypeInfo.forEach((a) => {
                if(!feeTypeIds.includes(a.fromTable)){
                    feeTypeIds.push(a.fromTable)
                    listFeeTypeTypes.push({id: a.fromTable, name: a.itemType});         
                }
            });
           setFeeTypeTypeOptions(OptionsHelper({ list: listFeeTypeTypes, includeBlank: true, includeBlankName: 'All' }));
        }
        setIsFeeTypeBusy(!_.isEmpty(feeTypeData) && (feeTypeData.status === 'processing' && (feeTypeData.scope === 'delete' || feeTypeData.scope === 'list')));

    }, [feeTypeData, props]);

    useEffect(() => {
        if(selectedFeeType && selectedFeeType.length > 0){
            var listInfo = feeTypeListData.filter((e) => e.fromTable == selectedFeeType);
            setFeeTypeListDataFiltered(listInfo);
        } else {
            setFeeTypeListDataFiltered(feeTypeListData);
        }
    }, [selectedFeeType, feeTypeListData]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        listRecords();
        feeTypeList({franchiseId: 0});
        productTemplateTypeList({ categoryName: 'ProductTemplateTypes' });

        setIsMounted(true);
    }, [isMounted, hasPermission, feeTypeList, productTemplateTypeList, listRecords, franchise]);

    useEffect(() => {
        dispatch(breadcrumbAddRoute({ key: 'franchise', parent: 'Accounting', text: 'Subscription/Fee Mapping', path: { event: () => setSelectedTab('subscriptions') } }));
        updateBreadcrumbs(selectedTab);
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch, selectedTab, updateBreadcrumbs]);

    return (
        <> 
            <SideMenu activeKey="accounting" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Subscription/Fee Mapping" />
                            <Panel>
                                <Panel.Body>
                                    <Tab.Container id='franchiseProductsTemplatesTabContainer' defaultActiveKey="subscriptions" activeKey={selectedTab} onSelect={tabSelectedHandler}>
                                        <Row className="clearfix">
                                            <Col sm={12}>
                                                <Nav bsStyle="pills">
                                                    <NavItem eventKey="subscriptions">Subscriptions</NavItem>
                                                    <NavItem eventKey="fees">Fee Mapping</NavItem>
                                                </Nav>
                                            </Col>
                                            <Col sm={12}>
                                                <Tab.Content animation>
                                                    {(selectedTab === 'subscriptions') &&
                                                        <Tab.Pane eventKey="subscriptions">
                                                            {
                                                                isEditing && <FranchiseProductTemplateModal
                                                                    show={isEditing}
                                                                    canEdit={canEdit}
                                                                    title={(!_.isEmpty(data) && !_.isEmpty(data.body)) ? 'Edit Instance Subscription' : 'Add Instance Subscription'}
                                                                    submitted={() => submittedHandler()}
                                                                    selected={(franchiseProduct) => selectedHandler(franchiseProduct)}
                                                                    assigned={() => assignedHandler()}
                                                                    cancelled={() => cancelledHandler()} />                    
                                                            }
                                                            <DocumentSetupList
                                                                compType="Subscription"
                                                                listData={listData}
                                                                sizePerPage={25}
                                                                isBusy={isBusy}
                                                                sorting={sorting}
                                                                columns={columns}
                                                                hideCopy={true}
                                                                hidePrint={true}
                                                                hideFilter={true}  
                                                                moreFilters={moreFilters(productTemplateTypeOptions)}   
                                                                permissionEdit={canEdit}                       
                                                                added={() => addedHandler()}
                                                                selected={(franchiseProduct) => selectedHandler(franchiseProduct)}
                                                                deleted={(franchiseProduct) => deletedHandler(franchiseProduct)}
                                                                refreshed={() => refreshedHandler()} />
                                                        </Tab.Pane>
                                                    }
                                                    {(selectedTab === 'fees') &&
                                                        <Tab.Pane eventKey="fees">
                                                            {
                                                                isFeeTypeEditing && <FeeTypeModal
                                                                    show={isFeeTypeEditing}
                                                                    canEdit={canEdit}
                                                                    feeTypeItem={feeTypeItem}
                                                                    title={(!_.isEmpty(feeTypeData) && !_.isEmpty(feeTypeData.body)) ? 'Edit Fee Type' : 'Add Fee Type'}
                                                                    submitted={() => submittedFeeTypeHandler()}
                                                                    selected={(feeType) => selectedFeeTypeHandler(feeType)}
                                                                    assigned={() => assignedFeeTypeHandler()}
                                                                    cancelled={() => cancelledFeeTypeHandler()} />                    
                                                            }
                                                            <DocumentSetupList
                                                                compType="Fee Types"
                                                                listData={feeTypeListDataFiltered}
                                                                sizePerPage={25}
                                                                isBusy={isFeeTypeBusy}
                                                                sorting={sorting_feetype}
                                                                columns={columns_feetype}
                                                                hideCopy={true}
                                                                hidePrint={true}
                                                                hideFilter={true}
                                                                hideAdd={true}
                                                                hideDelete={true}
                                                                moreFilters={typeFilters(feeTypeTypeOptions)}   
                                                                permissionEdit={canEdit}                       
                                                                selected={(feeType) => selectedFeeTypeHandler(feeType)}
                                                                refreshed={() => refreshedFeeTypeHandler()} />
                                                        </Tab.Pane>
                                                    }
                                                </Tab.Content>
                                            </Col>
                                        </Row>
                                    </Tab.Container>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(FranchiseProductsTemplates));
